import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import { useAdminGate } from "../../../utils/useAdminGate";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import CatalogEmptyState from "../../common/catalog/CatalogEmptyState";
import DetailAccentCard from "../../common/detail/DetailAccentCard";
import EditorFieldSection from "../../common/editor/EditorFieldSection";
import {
	editorFileLabelClassName,
	editorInputClassName,
	editorLabelClassName,
} from "../../common/editor/editorStyles";
import Footer from "../../common/footer/Footer";
import Header from "../../common/header/Header";
import DashboardSidebar from "../DashboardSidebar";
import AdminListCoverThumb from "../AdminListCoverThumb";
import {
	CAROUSEL_TARGET_OPTIONS,
	carouselItemEntityId,
	carouselItemSummary,
	seedEntityOptionsFromItem,
} from "./carouselItemTargets";
import ExternalUrlField from "./ExternalUrlField";
import AdminEntitySearchSelect from "./AdminEntitySearchSelect";
import { isValidExternalUrl, normalizeExternalUrl } from "./normalizeExternalUrl";

const emptyCarousel = { title: "", order: "0", is_visible: true };
const emptyItem = {
	target_type: "workshop",
	title: "",
	url: "",
	order: "0",
	image_file: null,
	workshop: "",
	webinar: "",
	video_clip: "",
	doctor: "",
};

export default function AdminHomepageCarousels() {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [carousels, setCarousels] = useState([]);
	const [carouselForm, setCarouselForm] = useState(emptyCarousel);
	const [editingCarouselId, setEditingCarouselId] = useState(null);
	const [carouselFormOpen, setCarouselFormOpen] = useState(false);
	const [selectedCarouselId, setSelectedCarouselId] = useState("");
	const [itemForm, setItemForm] = useState(emptyItem);
	const [editingItemId, setEditingItemId] = useState(null);
	const [itemFormOpen, setItemFormOpen] = useState(false);
	const [entityQuery, setEntityQuery] = useState("");
	const [entityOptions, setEntityOptions] = useState([]);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [loading, setLoading] = useState(false);
	const { ready, isAdmin } = useAdminGate();

	const loadCarousels = useCallback(() => {
		return api
			.homeCarouselAdminList({ token: LoginHelper.getToken() })
			.then((result) => {
				const list = Array.isArray(result) ? result : [];
				setCarousels(list);
				setSelectedCarouselId((current) =>
					current || String(list[0]?.id || "")
				);
				return list;
			})
			.catch((err) => {
				setError(err?.data?.message || "دریافت کروسل‌ها ناموفق بود");
				return [];
			});
	}, []);

	useEffect(() => {
		const resize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", resize);
		if (isAdmin) loadCarousels();
		return () => window.removeEventListener("resize", resize);
	}, [isAdmin, loadCarousels]);

	useEffect(() => {
		if (!itemFormOpen) return;
		const timer = window.setTimeout(() => {
			const q = entityQuery.trim();
			const token = LoginHelper.getToken();
			if (itemForm.target_type === "doctor") {
				api
					.doctorSearch({ query: q, page: 1 })
					.then((r) => setEntityOptions(r.doctors || []))
					.catch(() => setEntityOptions([]));
				return;
			}
			if (itemForm.target_type === "workshop") {
				api
					.workshopAdminList({ token, query: q, page: 1 })
					.then((r) => setEntityOptions(r.workshops || []))
					.catch(() => setEntityOptions([]));
				return;
			}
			if (itemForm.target_type === "webinar") {
				api
					.webinarAdminList({ token, query: q, page: 1 })
					.then((r) => setEntityOptions(r.webinars || []))
					.catch(() => setEntityOptions([]));
				return;
			}
			if (itemForm.target_type === "video_clip") {
				api
					.videoClipAdminList({ token, query: q, page: 1 })
					.then((raw) => {
						const clips = Array.isArray(raw) ? raw : raw?.clips || [];
						setEntityOptions(clips);
					})
					.catch(() => setEntityOptions([]));
			}
		}, 300);
		return () => window.clearTimeout(timer);
	}, [entityQuery, itemForm.target_type, itemFormOpen]);

	function saveCarousel(event) {
		event.preventDefault();
		setLoading(true);
		setError("");
		setNotice("");
		api
			.homeCarouselSave({
				token: LoginHelper.getToken(),
				...(editingCarouselId ? { carousel_id: editingCarouselId } : {}),
				...carouselForm,
				order: Number(carouselForm.order),
			})
			.then((result) => {
				setNotice(editingCarouselId ? "ردیف ویرایش شد" : "ردیف جدید ساخته شد");
				setCarouselForm(emptyCarousel);
				setEditingCarouselId(null);
				setCarouselFormOpen(false);
				setSelectedCarouselId(String(result.id));
				loadCarousels();
			})
			.catch((err) =>
				setError(err?.data?.message || "ذخیره ردیف ناموفق بود")
			)
			.finally(() => setLoading(false));
	}

	function saveItem(event) {
		event.preventDefault();
		if (!selectedCarouselId) {
			setError("اول یک ردیف کروسل انتخاب یا بسازید");
			return;
		}
		setLoading(true);
		setError("");
		setNotice("");
		const titleTrimmed = (itemForm.title ?? "").trim();
		const payload = {
			token: LoginHelper.getToken(),
			...(editingItemId ? { item_id: editingItemId } : {}),
			carousel: selectedCarouselId,
			target_type: itemForm.target_type,
			order: Number(itemForm.order),
			...(titleTrimmed ? { title: titleTrimmed } : {}),
			...(itemForm.image_file ? { image_file: itemForm.image_file } : {}),
		};
		if (itemForm.target_type === "url") {
			const url = normalizeExternalUrl(itemForm.url);
			if (!isValidExternalUrl(url)) {
				setError("آدرس لینک معتبر نیست (مثال: google.com)");
				setLoading(false);
				return;
			}
			payload.url = url;
		}
		if (itemForm.target_type === "doctor") {
			payload.doctor = itemForm.doctor;
		}
		if (itemForm.target_type === "workshop") {
			payload.workshop = itemForm.workshop;
		}
		if (itemForm.target_type === "webinar") {
			payload.webinar = itemForm.webinar;
		}
		if (itemForm.target_type === "video_clip") {
			payload.video_clip = itemForm.video_clip;
		}
		api
			.homeCarouselItemSave(payload)
			.then(() => {
				setNotice(editingItemId ? "آیتم ویرایش شد" : "آیتم اضافه شد");
				setEditingItemId(null);
				setItemForm(emptyItem);
				setItemFormOpen(false);
				setEntityQuery("");
				loadCarousels();
			})
			.catch((err) => {
				const base = err?.data?.message || "ذخیره آیتم ناموفق بود";
				const fieldErrors = err?.data?.errors;
				if (fieldErrors && typeof fieldErrors === "object") {
					const first = Object.entries(fieldErrors)[0];
					if (first) {
						const [field, msgs] = first;
						const msg = Array.isArray(msgs) ? msgs[0] : msgs;
						setError(`${base} (${field}: ${msg})`);
						return;
					}
				}
				setError(base);
			})
			.finally(() => setLoading(false));
	}

	function startEditItem(item) {
		setEditingItemId(item.id);
		setSelectedCarouselId(String(item.carousel));
		setItemForm({
			target_type: item.target_type || "doctor",
			title: item.title || "",
			url: item.url || "",
			order: String(item.order ?? 0),
			image_file: null,
			workshop: item.workshop ? String(item.workshop) : "",
			webinar: item.webinar ? String(item.webinar) : "",
			video_clip: item.video_clip ? String(item.video_clip) : "",
			doctor: item.doctor ? String(item.doctor) : "",
		});
		setEntityQuery(carouselItemSummary(item));
		setEntityOptions(seedEntityOptionsFromItem(item));
		setItemFormOpen(true);
	}

	if (!ready) return null;
	if (!isAdmin) return <Navigate to="/login/" replace />;

	const selectedCarousel = carousels.find(
		(c) => String(c.id) === String(selectedCarouselId)
	);

	return (
		<>
			<Helmet>
				<title>مدیریت ردیف‌های صفحه اصلی | همراهتیم</title>
			</Helmet>
			<div className="flex min-h-screen flex-col justify-between bg-[#F0F6FC]">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto max-w-[1200px] px-4">
							<Breadcrumb
								linkDataList={[
									{ label: "خانه", href: "/" },
									{ label: "داشبورد مدیریت", href: "/dashboard/" },
									{
										label: "ردیف‌های صفحه اصلی",
										href: "/dashboard/homepage-carousels/",
									},
								]}
							/>
						</div>
					</div>
					<main
						className={[
							"mx-auto flex w-full max-w-[1200px] gap-5 py-8",
							isDesktop ? "flex-row px-4" : "flex-col p-[10px]",
						].join(" ")}
					>
						<DashboardSidebar isDesktop={isDesktop} />
						<section className="flex flex-1 flex-col gap-6">
							<DetailAccentCard bodyClassName="p-5 md:p-6">
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div>
										<h1 className="font-Vazir-800 text-[22px] text-darkBlue md:text-[24px]">
											ردیف‌های صفحه اصلی
										</h1>
										<p className="mt-2 max-w-2xl font-Vazir-400 text-[14px] leading-relaxed text-darkBlue/60">
											هر ردیف یک عنوان و چند کارت (کارگاه، وبینار، چالش،
											درمانگر یا لینک) دارد. ترتیب ردیف‌ها و آیتم‌ها با
											عدد «ترتیب» مشخص می‌شود.
										</p>
									</div>
									<button
										type="button"
										onClick={() => {
											setCarouselFormOpen(true);
											setEditingCarouselId(null);
											setCarouselForm(emptyCarousel);
										}}
										className="rounded-[12px] bg-cta px-5 py-2.5 font-Vazir-700 text-[14px] text-white"
									>
										+ ردیف جدید
									</button>
								</div>

								{notice && (
									<p className="mt-4 rounded-[10px] bg-green-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-green-800">
										{notice}
									</p>
								)}
								{error && (
									<p className="mt-4 rounded-[10px] bg-red-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-[#cc0000]">
										{error}
									</p>
								)}

								<div className="mt-4 grid gap-3">
									{carousels.map((carousel) => (
										<article
											key={carousel.id}
											className={[
												"rounded-[12px] border p-4 transition",
												String(selectedCarouselId) === String(carousel.id)
													? "border-cta/40 bg-cta/5"
													: "border-darkBlue/10 bg-white",
											].join(" ")}
										>
											<div className="flex flex-wrap items-center justify-between gap-3">
												<button
													type="button"
													onClick={() =>
														setSelectedCarouselId(String(carousel.id))
													}
													className="text-right"
												>
													<h2 className="font-Vazir-700 text-[16px] text-darkBlue">
														{carousel.title}
														{!carousel.is_visible && (
															<span className="mr-2 text-[12px] text-red-600">
																(مخفی)
															</span>
														)}
													</h2>
													<p className="mt-1 font-Vazir-400 text-[12px] text-darkBlue/55">
														ترتیب {carousel.order} ·{" "}
														{carousel.items?.length || 0} آیتم
													</p>
												</button>
												<div className="flex flex-wrap gap-2">
													<button
														type="button"
														onClick={() => {
															setSelectedCarouselId(String(carousel.id));
															setItemFormOpen(true);
															setEditingItemId(null);
															setItemForm(emptyItem);
															setEntityQuery("");
														}}
														className="rounded-[10px] border border-cta/30 bg-white px-3 py-2 font-Vazir-600 text-[13px] text-cta"
													>
														+ آیتم
													</button>
													<button
														type="button"
														onClick={() => {
															setEditingCarouselId(carousel.id);
															setCarouselForm({
																title: carousel.title,
																order: String(carousel.order ?? 0),
																is_visible: carousel.is_visible,
															});
															setCarouselFormOpen(true);
														}}
														className="rounded-[10px] bg-lightCta px-3 py-2 font-Vazir-600 text-[13px] text-darkBlue"
													>
														ویرایش ردیف
													</button>
													<button
														type="button"
														onClick={() => {
															if (
																!window.confirm(
																	`ردیف «${carousel.title}» حذف شود؟`
																)
															)
																return;
															api
																.homeCarouselRemove({
																	token: LoginHelper.getToken(),
																	carousel_id: carousel.id,
																})
																.then(loadCarousels);
														}}
														className="rounded-[10px] border border-red-200 px-3 py-2 font-Vazir-600 text-[13px] text-red-600"
													>
														حذف
													</button>
												</div>
											</div>
											{String(selectedCarouselId) ===
												String(carousel.id) &&
												(carousel.items?.length ?? 0) > 0 && (
													<ul className="mt-3 grid gap-2 border-t border-darkBlue/10 pt-3">
														{carousel.items.map((item) => (
															<li
																key={item.id}
																className="flex flex-wrap items-center gap-3"
															>
																<AdminListCoverThumb
																	src={item.image_url}
																	showWhenEmpty
																/>
																<div className="min-w-0 flex-1">
																	<p className="font-Vazir-600 text-[14px] text-darkBlue">
																		{item.title ||
																			carouselItemSummary(item)}
																	</p>
																	<p className="font-Vazir-400 text-[12px] text-darkBlue/55">
																		{
																			CAROUSEL_TARGET_OPTIONS.find(
																				(o) =>
																					o.value === item.target_type
																			)?.label
																		}{" "}
																		· ترتیب {item.order}
																	</p>
																</div>
																<button
																	type="button"
																	onClick={() => startEditItem(item)}
																	className="rounded-[10px] bg-lightCta px-3 py-2 font-Vazir-600 text-[13px]"
																>
																	ویرایش
																</button>
																<button
																	type="button"
																	onClick={() => {
																		if (
																			!window.confirm("آیتم حذف شود؟")
																		)
																			return;
																		api
																			.homeCarouselItemRemove({
																				token:
																					LoginHelper.getToken(),
																				item_id: item.id,
																			})
																			.then(loadCarousels);
																	}}
																	className="rounded-[10px] border border-red-200 px-3 py-2 text-[13px] text-red-600"
																>
																	حذف
																</button>
															</li>
														))}
													</ul>
												)}
										</article>
									))}
									{carousels.length === 0 && (
										<CatalogEmptyState
											message="هنوز ردیفی برای صفحه اصلی نیست."
											hint="با «ردیف جدید» یک بخش (مثلاً کارگاه‌های پیشنهادی) بسازید و آیتم اضافه کنید."
											actionLabel="ساخت ردیف"
											onAction={() => setCarouselFormOpen(true)}
										/>
									)}
								</div>
							</DetailAccentCard>

							{carouselFormOpen && (
								<DetailAccentCard bodyClassName="p-5 md:p-6">
									<h2 className="font-Vazir-800 text-[20px] text-darkBlue">
										{editingCarouselId ? "ویرایش ردیف" : "ردیف جدید"}
									</h2>
									<form onSubmit={saveCarousel} className="mt-4 flex flex-col gap-4">
										<EditorFieldSection title="عنوان ردیف">
											<input
												required
												value={carouselForm.title}
												onChange={(e) =>
													setCarouselForm({
														...carouselForm,
														title: e.target.value,
													})
												}
												placeholder="مثلاً: کارگاه‌های پیشنهادی"
												className={editorInputClassName}
											/>
											<label className="flex items-center gap-2 font-Vazir-400 text-[14px]">
												ترتیب
												<input
													type="number"
													min="0"
													value={carouselForm.order}
													onChange={(e) =>
														setCarouselForm({
															...carouselForm,
															order: e.target.value,
														})
													}
													className={`${editorInputClassName} max-w-[120px]`}
												/>
											</label>
											<label className="flex items-center gap-2 font-Vazir-400 text-[14px]">
												<input
													type="checkbox"
													checked={carouselForm.is_visible}
													onChange={(e) =>
														setCarouselForm({
															...carouselForm,
															is_visible: e.target.checked,
														})
													}
												/>
												نمایش در صفحه اصلی
											</label>
										</EditorFieldSection>
										<div className="flex gap-2">
											<button
												type="submit"
												disabled={loading}
												className="rounded-[12px] bg-cta px-6 py-3 font-Vazir-700 text-white disabled:opacity-60"
											>
												ذخیره
											</button>
											<button
												type="button"
												onClick={() => {
													setCarouselFormOpen(false);
													setEditingCarouselId(null);
												}}
												className="rounded-[12px] border px-6 py-3 font-Vazir-600"
											>
												انصراف
											</button>
										</div>
									</form>
								</DetailAccentCard>
							)}

							{itemFormOpen && selectedCarousel && (
								<DetailAccentCard bodyClassName="p-5 md:p-6">
									<h2 className="font-Vazir-800 text-[20px] text-darkBlue">
										{editingItemId ? "ویرایش آیتم" : "آیتم جدید"} —{" "}
										{selectedCarousel.title}
									</h2>
									<form onSubmit={saveItem} className="mt-4 flex flex-col gap-4">
										<EditorFieldSection title="مقصد کارت">
											<label className={editorLabelClassName}>نوع</label>
											<select
												value={itemForm.target_type}
												onChange={(e) => {
													setItemForm({
														...emptyItem,
														target_type: e.target.value,
														order: itemForm.order,
														title: itemForm.title,
														image_file: itemForm.image_file,
													});
													setEntityQuery("");
												}}
												className={editorInputClassName}
											>
												{CAROUSEL_TARGET_OPTIONS.map((opt) => (
													<option key={opt.value} value={opt.value}>
														{opt.label}
													</option>
												))}
											</select>
											{itemForm.target_type === "url" && (
												<ExternalUrlField
													value={itemForm.url}
													onChange={(url) =>
														setItemForm({ ...itemForm, url })
													}
												/>
											)}
											{itemForm.target_type !== "url" && (
												<AdminEntitySearchSelect
													required
													value={carouselItemEntityId(itemForm)}
													options={entityOptions}
													searchQuery={entityQuery}
													onSearchQueryChange={setEntityQuery}
													onChange={(id) => {
														const key = itemForm.target_type;
														setItemForm({
															...itemForm,
															doctor: "",
															workshop: "",
															webinar: "",
															video_clip: "",
															[key]: id,
														});
													}}
												/>
											)}
											<label className={editorLabelClassName}>
												عنوان روی کارت (اختیاری)
											</label>
											<input
												value={itemForm.title}
												onChange={(e) =>
													setItemForm({ ...itemForm, title: e.target.value })
												}
												placeholder="خالی = عنوان خود محتوا"
												className={editorInputClassName}
											/>
											<label className={editorFileLabelClassName}>
												<span>کاور سفارشی (اختیاری برای کارگاه/وبینار/چالش)</span>
												<input
													type="file"
													accept="image/*"
													onChange={(e) =>
														setItemForm({
															...itemForm,
															image_file: e.target.files?.[0] || null,
														})
													}
												/>
											</label>
											<label className="flex items-center gap-2 font-Vazir-400 text-[14px]">
												ترتیب در ردیف
												<input
													type="number"
													min="0"
													value={itemForm.order}
													onChange={(e) =>
														setItemForm({
															...itemForm,
															order: e.target.value,
														})
													}
													className={`${editorInputClassName} max-w-[120px]`}
												/>
											</label>
										</EditorFieldSection>
										<div className="flex gap-2">
											<button
												type="submit"
												disabled={loading}
												className="rounded-[12px] bg-cta px-6 py-3 font-Vazir-700 text-white disabled:opacity-60"
											>
												{editingItemId ? "ذخیره آیتم" : "افزودن آیتم"}
											</button>
											<button
												type="button"
												onClick={() => {
													setItemFormOpen(false);
													setEditingItemId(null);
												}}
												className="rounded-[12px] border px-6 py-3 font-Vazir-600"
											>
												انصراف
											</button>
										</div>
									</form>
								</DetailAccentCard>
							)}
						</section>
					</main>
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
